import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Container,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Box,
  Grid
} from '@mui/material';
import { RadioButtonChecked, RadioButtonUnchecked } from '@mui/icons-material';
import { fetchUserProgress, fetchUsers } from '../../features/users/usersSlice';
import { fetchClasses } from '../../features/techniques/techniquesSlice';
import { styled } from '@mui/material/styles';

const ProgressButtonsContainer = styled('div')`
  display: flex;
  gap: 8px;
`;

const ProgressButton = styled(({ filled, date, ...props }) => (
  <Tooltip title={date ? new Date(date).toLocaleDateString() : ''}>
    <span>
      <IconButton
        disabled
        size="small"
        {...props}
      >
        {filled ? 
          <RadioButtonChecked color={props.isExtra ? "error" : "primary"} /> : 
          <RadioButtonUnchecked />
        }
      </IconButton>
    </span>
  </Tooltip>
))`
  padding: 4px;
`;

const UserDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const { 
    selectedUserProgress, 
    loading, 
    error, 
    users, 
    userStats  // Changed from selectedUserStats
  } = useSelector((state) => state.users);
  const { isAuthenticated, user: currentUser } = useSelector((state) => state.auth);
  const { userProgress: currentUserProgress } = useSelector((state) => state.techniques);
  
  const selectedUser = users.find(u => u.id.toString() === userId);
  const stats = userStats?.[userId];  // Get stats for this specific user

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Fetch users if not already loaded
    if (users.length === 0) {
      dispatch(fetchUsers());
    }

    if (userId) {
      dispatch(fetchUserProgress(userId));
      dispatch(fetchClasses());
    }
  }, [dispatch, userId, isAuthenticated, navigate, users.length]);

  // Add debug logging
  useEffect(() => {
    console.log('UserDetail state:', {
      userId,
      selectedUser,
      stats,
      selectedUserProgress,
      currentUserProgress,
      loading,
      error
    });
  }, [userId, selectedUser, stats, selectedUserProgress, currentUserProgress, loading, error]);

  // Add debug logging before the render
  console.log('Current state:', { selectedUserProgress, currentUserProgress, loading, error });

  if (loading || !selectedUserProgress || !Array.isArray(selectedUserProgress)) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!selectedUser) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">
          <Typography variant="h6">User Not Found</Typography>
        </Alert>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">
          <Typography variant="h6">Error Loading Progress</Typography>
          <Typography>{error}</Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h5" gutterBottom>
          {selectedUser?.username}'s Progress
        </Typography>
        {/* <Typography variant="subtitle1" color="text.secondary">
          Classes Completed: {selectedUserStats?.classesCompleted} • 
          Techniques Learned: {selectedUserStats?.techniquesLearned} • 
          Stripes: {selectedUserStats?.stripes} 
        </Typography> */}
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6">Classes Completed</Typography>
            <Typography variant="h3">{stats?.classesCompleted || 0}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6">Stripes</Typography>
            <Typography variant="h3">{stats?.stripes || 0}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6">Techniques Learned</Typography>
            <Typography variant="h3">{stats?.techniquesLearned || 0}</Typography>
          </Paper>
        </Grid>
        
      </Grid>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Class #</TableCell>
              <TableCell>Technique</TableCell>
              <TableCell>{selectedUser?.username}'s Progress</TableCell>
              <TableCell>Your Progress</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {selectedUserProgress.map((classData) => {
              const currentUserClassData = currentUserProgress[classData.class_number];
              
              return (
                <TableRow 
                  key={classData.class_number}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <TableCell>
                    Class {classData.class_number}
                  </TableCell>
                  <TableCell>
                    {classData.techniques.map((technique, index) => (
                      <div key={technique.id}>
                        {technique.name}
                        {technique.details && (
                          <Typography variant="body2" color="text.secondary">
                            {technique.details}
                          </Typography>
                        )}
                        {index < classData.techniques.length - 1 && <br />}
                      </div>
                    ))}
                  </TableCell>
                  <TableCell>
                    <ProgressButtonsContainer>
                      {[0, 1, 2, 3, 4].map((index) => {
                        const dates = classData.class_dates || [];
                        return (
                          <ProgressButton
                            key={index}
                            filled={dates[index]}
                            date={dates[index]}
                            isExtra={index >= 3}
                          />
                        );
                      })}
                    </ProgressButtonsContainer>
                  </TableCell>
                  <TableCell>
                    <ProgressButtonsContainer>
                      {[0, 1, 2, 3, 4].map((index) => {
                        const dates = currentUserClassData?.dates || [];
                        return (
                          <ProgressButton
                            key={index}
                            filled={dates[index]}
                            date={dates[index]}
                            isExtra={index >= 3}
                          />
                        );
                      })}
                    </ProgressButtonsContainer>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default UserDetail; 